import { useReducer, useCallback, useMemo } from 'react';

import type { GOOGLE_EXPORT_ERRORS } from '../config/googleExportErrors.ts';
import type { ProcessErrors } from '../config/processErrors.ts';
import type { GoogleDrivePickerDoc } from '../services/GoogleAPIs/GoogleDrivePicker.ts';
import type { OneDriveFileType } from '../services/OneDriveAPIs/OneDriveTypes.ts';
import { getFileNameWithoutExtension } from '../utils/getFileNameWithoutExtension.ts';

export type Doc = {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	document: GoogleDrivePickerDoc | File | OneDriveFileType;
	nameWithoutExt: string;
	completed: boolean;
	selected: boolean;
	error: DocError | null;
	controller?: AbortController;
	id?: string;
	size?: number;
};

type Action = {
	type: string;
	[key: string]: any;
};

export enum ErrorRoles {
	ERROR = 'ERROR',
	WARNING = 'WARNING',
}

export type DocError = {
	type: Errors;
	role: ErrorRoles;
};

type Actions = {
	uploadDocsStart: (documents: GoogleDrivePickerDoc[] | File[] | OneDriveFileType[]) => void;
	updateDocuments: (documents: GoogleDrivePickerDoc[] | File[] | OneDriveFileType[]) => void;
	uploadDocSuccess: (name: string, id: string) => void;
	uploadDocFailure: (name: string, error: DocError) => void;
	uploadDocSelect: (name: string) => void;
	uploadDocController: (name: string, controller: AbortController) => void;
	uploadDocSize: (name: string, size: number) => void;
	uploadDocClearErrors: () => void;
};

type State = {
	docs: Doc[];
};

export type DocReducerType = {
	docs: Doc[];
	docActions: Actions;
};

export enum DocErrors {
	TYPE_ERROR = 'TYPE_ERROR',
	SIZE_LOCAL_ERROR = 'SIZE_LOCAL_ERROR',
	SIZE_GD_ERROR = 'SIZE_GD_ERROR',
	REQUEST_ABORTED_ERROR = 'REQUEST_ABORTED_ERROR',
	FAILED_UPLOAD_ERROR = 'FAILED_UPLOAD_ERROR',
	NOT_UNIQ_TITLE = 'NOT_UNIQ_TITLE',
	STRICT_FORMAT_ERROR = 'STRICT_FORMAT_ERROR',
	PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
}

type Errors = DocErrors | ProcessErrors | GOOGLE_EXPORT_ERRORS;

export enum DocAction {
	UPLOAD_DOCS_START = 'UPLOAD_DOCS_START',
	UPDATE_DOCUMENTS = 'UPDATE_DOCUMENTS',
	UPLOAD_DOC_SUCCESS = 'UPLOAD_DOC_SUCCESS',
	UPLOAD_DOC_FAILURE = 'UPLOAD_DOC_FAILURE',
	UPLOAD_DOC_SELECT = 'UPLOAD_DOC_SELECT',
	UPLOAD_DOC_CONTROLLER = 'UPLOAD_DOC_CONTROLLER',
	UPLOAD_DOC_SIZE = 'UPLOAD_DOC_SIZE',
	UPLOAD_DOC_CLEAR_ERRORS = 'UPLOAD_DOC_CLEAR_ERRORS',
}

const setDocuments = (documents: (GoogleDrivePickerDoc | File | OneDriveFileType)[]) => {
	return documents.map((doc: GoogleDrivePickerDoc | File | OneDriveFileType) => {
		return {
			document: doc,
			nameWithoutExt: getFileNameWithoutExtension(doc.name),
			completed: false,
			selected: true,
			error: null,
			size: doc.size,
		};
	});
};

const updateDocuments = (
	currentDocs: Doc[],
	docs: GoogleDrivePickerDoc[] | File[] | OneDriveFileType[] | null[],
): Doc[] => {
	return currentDocs.map((currentDoc) => {
		const matchedDocument = docs.find((doc) => doc?.name === currentDoc?.document.name);

		if (matchedDocument) {
			return {
				...currentDoc,
				document: matchedDocument,
			};
		}
		return currentDoc;
	});
};

const setDocUplodedData = (docs: Doc[], name: string, id: string): Doc[] => {
	return docs.map((doc) => {
		if (name === doc.nameWithoutExt) {
			return {
				...doc,
				completed: true,
				id,
			};
		}
		return { ...doc };
	});
};

const selectDocument = (docs: Doc[], name: string): Doc[] => {
	return docs.map((doc) => {
		if (name === doc.nameWithoutExt) {
			return {
				...doc,
				selected: !doc.selected,
			};
		}
		return { ...doc };
	});
};

const setDocumentError = (docs: Doc[], name: string, error: DocError): Doc[] => {
	return docs.map((doc) => {
		if (
			name === doc.document.name ||
			(name === doc.nameWithoutExt && doc.error?.type !== DocErrors.REQUEST_ABORTED_ERROR)
		) {
			return {
				...doc,
				completed: true,
				error: {
					...error,
				},
			};
		}
		return { ...doc };
	});
};

const setDocumentController = (docs: Doc[], name: string, controller: AbortController): Doc[] => {
	return docs.map((doc) => {
		if (name === doc.document.name) {
			return {
				...doc,
				controller,
			};
		}
		return { ...doc };
	});
};

const setDocumentSize = (docs: Doc[], name: string, size: number): Doc[] => {
	return docs.map((doc) => {
		if (name === doc.document.name) {
			return {
				...doc,
				size,
			};
		}
		return { ...doc };
	});
};

const setDocumentsErrorNull = (docs: Doc[]): Doc[] => {
	return docs.map((doc) => {
		return {
			...doc,
			completed: false,
			error: null,
		};
	});
};

export const useUploadReducer = (): DocReducerType => {
	//todo: definining initial state here means the reducer useCallback will change every render
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialState: State = {
		docs: [],
	};

	const reducer = useCallback(
		(state: State, action: Action): State => {
			switch (action.type) {
				case DocAction.UPLOAD_DOCS_START: {
					return {
						docs: setDocuments(action.documents),
					};
				}
				case DocAction.UPDATE_DOCUMENTS: {
					return {
						docs: updateDocuments(state.docs, action.documents),
					};
				}
				case DocAction.UPLOAD_DOC_SUCCESS: {
					return {
						docs: setDocUplodedData(state.docs, action.name, action.id),
					};
				}
				case DocAction.UPLOAD_DOC_FAILURE: {
					const { name, error } = action;
					return {
						docs: setDocumentError(state.docs, name, error),
					};
				}
				case DocAction.UPLOAD_DOC_SELECT: {
					return {
						docs: selectDocument(state.docs, action.name),
					};
				}
				case DocAction.UPLOAD_DOC_CONTROLLER: {
					const { name, controller } = action;
					return {
						docs: setDocumentController(state.docs, name, controller),
					};
				}
				case DocAction.UPLOAD_DOC_SIZE: {
					const { name, size } = action;
					return {
						docs: setDocumentSize(state.docs, name, size),
					};
				}
				case DocAction.UPLOAD_DOC_CLEAR_ERRORS: {
					return {
						docs: setDocumentsErrorNull(state.docs),
					};
				}
				default:
					return initialState;
			}
		},
		[initialState],
	);

	const [{ docs }, dispatch] = useReducer(reducer, initialState);

	const docActions = useMemo(
		() => ({
			uploadDocsStart: (documents: GoogleDrivePickerDoc[] | File[] | OneDriveFileType[]) => {
				dispatch({ type: DocAction.UPLOAD_DOCS_START, documents });
			},
			updateDocuments: (documents) => {
				dispatch({ type: DocAction.UPDATE_DOCUMENTS, documents });
			},
			uploadDocSuccess: (name: string, id: string) => {
				dispatch({ type: DocAction.UPLOAD_DOC_SUCCESS, name, id });
			},
			uploadDocFailure: (name: string, error: DocError) => {
				dispatch({ type: DocAction.UPLOAD_DOC_FAILURE, name, error });
			},
			uploadDocSelect: (name: string) => {
				dispatch({ type: DocAction.UPLOAD_DOC_SELECT, name });
			},
			uploadDocController: (name: string, controller: AbortController) => {
				dispatch({ type: DocAction.UPLOAD_DOC_CONTROLLER, name, controller });
			},
			uploadDocSize: (name: string, size: number) => {
				dispatch({ type: DocAction.UPLOAD_DOC_SIZE, name, size });
			},
			uploadDocClearErrors: () => {
				dispatch({ type: DocAction.UPLOAD_DOC_CLEAR_ERRORS });
			},
		}),
		[],
	);

	return {
		docs,
		docActions,
	};
};
